import NextLink from 'next/link'
import Head from 'next/head'

import Typography from '@components/Typography/Typography'
import Button from '@components/Button/Button'
import { useTrackNavigationEvent, CurrentPathTypeEnum } from '@modules/analytics'
import * as S from './MissionPage.styles'
import VideoPlayer from '@components/VideoPlayer/VideoPlayer'

const LOKRE_TRAILER_CLOUDINARY_ID = 'fc33fb1a-045f-40b7-9d25-e0fd19313e6b'
const DJ_KHALED_SAMPLE_FEEDBACK_CLOUDINARY_ID = '89a8b40e-6b1a-4e53-9f83-e5ebea4b9c65'

const MissionPage: React.FC = () => {
  useTrackNavigationEvent({
    currentPathType: CurrentPathTypeEnum.MissionPage,
  })

  return (
    <>
      <Head>
        <meta
          name="description"
          content="Unveiling Protégé: Be heard. Get discovered."
          key="description"
        />
        <meta name="keywords" content="Protégé, Protege, Protege App, Our Mission" key="keywords" />

        <meta name="og:type" property="og:type" content="website" key="og:type" />
        <meta
          name="og:description"
          content="Unveiling Protégé: Be heard. Get discovered."
          key="og:description"
        />
        <meta property="og:url" content="https://www.protege.app/blog/mission" key="og:url" />
        <meta property="og:title" content="Protégé: Be heard. Get discovered." key="og:title" />
        <meta
          property="og:image"
          content="https://res.cloudinary.com/protege-app/video/upload/c_scale,w_640,so_31/v1647400288/fc33fb1a-045f-40b7-9d25-e0fd19313e6b.png"
          key="og:image"
        />
        <meta
          property="og:video"
          content="https://res.cloudinary.com/protege-app/video/upload/c_scale,w_640/v1647400288/fc33fb1a-045f-40b7-9d25-e0fd19313e6b.mp4"
          key="og:video"
        />
        <meta
          property="og:video:secure_url"
          content="https://res.cloudinary.com/protege-app/video/upload/c_scale,w_640/v1647400288/fc33fb1a-045f-40b7-9d25-e0fd19313e6b.mp4"
          key="og:video:secure_url"
        />
        <meta property="og:video:type" content="video/mp4" key="og:video:type" />
        <meta property="og:video:width" content="640" key="og:video:width" />
        <meta property="og:video:height" content="360" key="og:video:height" />

        <meta name="twitter:card" content="summary_large_image" key="twitter:card" />
        <meta name="twitter:site" content="@protege" key="twitter:site" />
        <meta name="twitter:domain" content="protege.app" key="twitter:domain" />
        <meta name="twitter:title" content="Protégé" key="twitter:title" />
        <meta
          name="twitter:image"
          content="https://res.cloudinary.com/protege-app/video/upload/c_scale,w_640,so_31/v1647400288/fc33fb1a-045f-40b7-9d25-e0fd19313e6b.png"
          key="twitter:image"
        />
      </Head>
      <S.PageWrapper>
        <S.Content>
          <S.Section>
            <Typography color="darkerBlue" variant="H800" uppercase>
              Unveiling Protégé: Be heard. Get discovered.
            </Typography>
          </S.Section>

          <S.Section>
            <Typography color="darkerBlue" variant="APPLICATION_TITLE_OVERLAY_LARGE" uppercase>
              Talent is distributed globally, but opportunity is not
            </Typography>
            <S.SectionContent>
              <S.SectionParagraph>
                <Typography color="primaryDarkContrast" variant="BODY_LARGE">
                  The reality of life is that we are all born into a lottery of where we grow up,
                  who our parents are, and what connections we have.
                </Typography>
              </S.SectionParagraph>
              <S.SectionParagraph>
                <Typography color="primaryDarkContrast" variant="BODY_LARGE">
                  Hard work and grit matter, yet many industries still have gatekeepers who control
                  access to the elite opportunities — especially in music, entertainment, and
                  business. To break into these fields, you need some form of introduction or
                  connection.
                </Typography>
              </S.SectionParagraph>
              <S.SectionParagraph>
                <Typography color="primaryDarkContrast" variant="BODY_LARGE">
                  The most successful people have a story about a mentor who changed their life.
                  Someone who showed them the ropes or took a shot on them. But it can be hard to
                  find those people if you don&apos;t have access to the right networks. This is why
                  we created Protégé.
                </Typography>
              </S.SectionParagraph>
              <S.SectionParagraph>
                <Typography color="primaryDarkContrast" variant="BODY_LARGE">
                  Meet Liz Lokre, a 27 year old aspiring singer who used Protege to get her big
                  break.
                </Typography>
              </S.SectionParagraph>
              <S.VideoContainer>
                <VideoPlayer
                  cloudinaryPublicId={LOKRE_TRAILER_CLOUDINARY_ID}
                  type="APPLICATION_VIDEO"
                  width={'100%'}
                  height={'100%'}
                  hidePlayButton={true}
                  showControls={true}
                  transformation={{ crop: 'fill', width: 1280, height: 720, fetch_format: 'mp4' }}
                />
              </S.VideoContainer>
            </S.SectionContent>
          </S.Section>
          <S.Section>
            <Typography color="darkerBlue" variant="APPLICATION_TITLE_OVERLAY_LARGE" uppercase>
              Protégé - get in touch with anyone in the world
            </Typography>
            <S.SectionContent>
              <S.SectionParagraph>
                <Typography color="primaryDarkContrast" variant="BODY_LARGE">
                  Our mission is to democratize access to your dream career. We help students be
                  heard and get discovered. We believe that every person deserves a shot at proving
                  themselves.
                </Typography>
              </S.SectionParagraph>

              <S.SectionParagraph>
                <Typography color="primaryDarkContrast" variant="BODY_LARGE">
                  For the first time ever, you can submit a 60 second video to the best experts in
                  the world and get a guaranteed video response and a shot at a life-changing
                  opportunity. You can send DJ Khaled a hip hop beat for his next album; Bebe Rexha
                  a vocal melody you are working on; or Jason Alexander your monologue you&apos;ve
                  been trying to perfect.
                </Typography>
              </S.SectionParagraph>

              <S.SectionParagraph>
                <Typography color="primaryDarkContrast" variant="BODY_LARGE">
                  Check out this up and coming music producer Aaron Green who sent his track to DJ
                  Khaled on Protégé.
                </Typography>
              </S.SectionParagraph>

              <S.SectionParagraph>
                <S.VideoContainer>
                  <VideoPlayer
                    width={'75%'}
                    cloudinaryPublicId={DJ_KHALED_SAMPLE_FEEDBACK_CLOUDINARY_ID}
                    type="APPLICATION_VIDEO"
                  />
                </S.VideoContainer>
              </S.SectionParagraph>
              <S.SectionParagraph>
                <Typography color="primaryDarkContrast" variant="BODY_LARGE">
                  With a diverse group of experts from multiple industries, our platform is like
                  American Idol meets Project Runway meets Shark Tank. But unlike those mass-market
                  shows, we are able to go so much deeper, creating opportunities in the narrowest
                  of categories, from R&B to Comedy, and forging real relationships between experts
                  and students.
                </Typography>
              </S.SectionParagraph>
              <S.SectionParagraph>
                <Typography color="primaryDarkContrast" variant="BODY_LARGE">
                  Application fees range from $10 to $200 depending on the expert. If you can&apos;t
                  afford the fee for the feedback video, you can apply for a scholarship by getting
                  upvoted by your friends and family.
                </Typography>
              </S.SectionParagraph>
              <S.SectionParagraph>
                <Typography color="primaryDarkContrast" variant="BODY_LARGE">
                  If you&apos;re an expert and want to learn more about how to join Protégé, email
                  us at <a href="mailto: experts@protege.app">experts@protege.app</a>.
                </Typography>
              </S.SectionParagraph>
            </S.SectionContent>
          </S.Section>
          {/* <S.Section>
            <Typography color="darkerBlue" variant="APPLICATION_TITLE_OVERLAY_LARGE" uppercase>
              Full circle
            </Typography>
            <S.SectionContent>
              <S.SectionParagraph>
                <Typography color="primaryDarkContrast" variant="BODY_LARGE">
                  We both struggled to be heard and get discovered. Jackson put his music
                  aspirations aside when it seemed impossible to break through. Mike struggled to
                  break into the start-up world coming from enterprise tech. We consider ourselves
                  both lucky and persistent enough to find mentors who believed in us and helped set
                  us up for success.
                </Typography>
              </S.SectionParagraph>
              <S.SectionParagraph>
                <Typography color="primaryDarkContrast" variant="BODY_LARGE">
                  Now, as founders, we have a chance to build the platform we needed earlier in our
                  careers. We are building a more open, accessible world - where no matter how out
                  of reach an expert may seem, anyone can get 60 seconds of their time to prove
                  themselves, disrupting the traditional power systems that have historically held
                  many back.
                </Typography>
              </S.SectionParagraph>
              <S.SectionParagraph>
                <Typography color="primaryDarkContrast" variant="BODY_LARGE">
                  And it&apos;s working! It&apos;s been unbelievably uplifting for our company to
                  follow along the stories of unknown musicians shooting their shot with the biggest
                  names in music and then to be selected for life-altering opportunities.
                </Typography>
              </S.SectionParagraph>
            </S.SectionContent>
          </S.Section>
          <S.Section>
          <Typography color="darkerBlue" variant="APPLICATION_TITLE_OVERLAY_LARGE" uppercase>
            We are so grateful to our experts and investors who believe in us.
          </Typography>
          <S.SectionContent>
            <S.SectionParagraph>
              <Typography color="primaryDarkContrast" variant="BODY_LARGE">
                We are excited to announce that Sequoia is leading our $8.5M Seed round alongside TQ
                Ventures (the firm founded by Scooter Braun, Schuster Tanger, and Andrew Marks), DJ
                Khaled, Lionel Richie, Will Smith, Roc Nation, Tate McCrae, Gryffin, Ben Simmons,
                Jason Alexander, Steven Galanis (CEO of Cameo), Tony Xu (CEO of Doordash).
              </Typography>
            </S.SectionParagraph>
            <S.SectionParagraph>
              <Typography color="primaryDarkContrast" variant="BODY_LARGE">
                We want to thank all of our angel investors who believe in us from the entertainment
                world including Diplo and his manager Andrew McInnes, Range Media Partners, Brian
                Kelley from Florida Georgia Line and his manager Bruce Kalmick, Tyga, Sam Feldt,
                Lupe Fiasco, Darkchild, Mustard, Harv and his manager CJ Cook, Felisha Harvey, Baby
                Ariel, Lance Stewart, Cody Ko, Grayson Allen, Lydia Asrat (Doja Cat&apos;s
                co-manager), Sylvia Rhone (CEO of Epic Records), Indie Pop, Demarcus Beasley, Darren
                Glover, Adriana Arce, Jeanine McClean, Bruce Eskowitz (COO of Redlight Management),
                Carol Yumkas, and more.
              </Typography>
            </S.SectionParagraph>
            <S.SectionParagraph>
              <Typography color="primaryDarkContrast" variant="BODY_LARGE">
                We are also excited to have support from angels in the business world including
                Anthony Pompliano, Li Jin, Matt Maloney (CEO of Grubhub), Spencer Rascoff
                (co-founder of Zillow), Nick Green (CEO of Thrive Market), CTO/CFO/and CPO of Cameo,
                Shreyas Dosi, Eugene Wei, Ankur Nagpal (CEO of Teachable), Brian Barnes (CEO of M1
                Finance), Matt Rutler (Masterclass), Josh Stech (CEO of Sundae), Chapter One, Animal
                Capital, Abe Burns, Greg Drobnik & Mike Wortsman, Abdullah Janahi, our former
                co-workers at Chicago Ventures, Creed Media, Derek Dolin, Evan Wray, Eytan Elbaz,
                Nick Gross, Nikola Lahcanski, Carter Reum, Starting Line, Scott Lamson, Stefan
                Heinrich (CEO of Mayk), Vijen Patel, Denmark West, Scott Wood, and more.
              </Typography>
            </S.SectionParagraph>
          </S.SectionContent>
        </S.Section> */}
          <S.Section>
            <Typography color="darkerBlue" variant="APPLICATION_TITLE_OVERLAY_LARGE" uppercase>
              Full circle
            </Typography>
            <S.SectionContent>
              <S.SectionParagraph>
                <Typography color="primaryDarkContrast" variant="BODY_LARGE">
                  We both struggled to be heard and get discovered. Jackson put his music
                  aspirations aside when it seemed impossible to break through. Mike struggled to
                  break into the start-up world coming from enterprise tech. We consider ourselves
                  both lucky and persistent enough to find mentors who believed in us and helped set
                  us up for success.
                </Typography>
              </S.SectionParagraph>
              <S.SectionParagraph>
                <Typography color="primaryDarkContrast" variant="BODY_LARGE">
                  Now, as founders, we have a chance to build the platform we needed earlier in our
                  careers. We are building a more open, accessible world—where no matter how out of
                  reach an expert may seem, anyone can get 60 seconds of their time to prove
                  themselves, disrupting the traditional power systems that have historically held
                  many back. And it&apos;s working! The stories of our students are deeply
                  inspiring.
                </Typography>
              </S.SectionParagraph>

              <S.SectionParagraph>
                <Typography color="primaryDarkContrast" variant="BODY_LARGE">
                  Thank you so much to all of our early experts, investors, and teammates who have
                  helped us bring this vision to life.
                </Typography>
              </S.SectionParagraph>

              <S.SectionParagraph>
                <Typography color="primaryDarkContrast" variant="BODY_LARGE">
                  If you are struggling to be heard, if you have a dream but need help getting
                  there, or if you want to help us build this company, come&nbsp;
                  <a
                    target="_blank"
                    href="https://protege.notion.site/Careers-at-Prot-g-08a4ebf7672b4e51a1ba4cf1af2e082d"
                    rel="noopener noreferrer"
                  >
                    join us
                  </a>
                  &nbsp;at Protégé, where anyone can be heard and get discovered.
                </Typography>
              </S.SectionParagraph>
              <S.SectionParagraph>
                <Typography color="primaryDarkContrast" variant="BODY_LARGE">
                  Sincerely,
                  <br />
                  Jackson and Mike
                </Typography>
              </S.SectionParagraph>
              <img
                width={'100%'}
                src="https://res.cloudinary.com/protege-app/image/upload/c_scale,w_800/v1647322824/core/founders-headshot.jpg"
                alt="headshot"
              />
            </S.SectionContent>
          </S.Section>
          <S.Section>
            <S.CallToAction>
              <NextLink href="/category">
                <Button size="large" variant="primary">
                  Discover opportunities
                </Button>
              </NextLink>
            </S.CallToAction>
          </S.Section>
        </S.Content>
      </S.PageWrapper>
    </>
  )
}

export default MissionPage
